// src/components/User/Calendar/EventCalendar.js

import React, { useEffect, useState, useMemo } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './EventCalendar.css';
import axios from '../api/axios'; // Assuming correct path to axios
import {
  format,
  isSameDay,
  parseISO,
  isToday,
  isAfter,
  isBefore,
  startOfMonth,
  startOfDay
} from 'date-fns';

const EventCalendar = () => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(startOfMonth(new Date()));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      // GET /calendar fetches all events sorted by startDate ASC
      const res = await axios.get('/calendar');
      setEvents(res.data);
    } catch (err) {
      console.error('Error fetching calendar events:', err);
    } finally {
      setLoading(false);
    }
  };

  // --- Filtering and Sorting Logic ---

  const today = startOfDay(new Date());

  const getEventStatus = (dateString) => {
    const eventDate = parseISO(dateString);
    if (isSameDay(eventDate, today)) return 'TODAY';
    if (isBefore(eventDate, today)) return 'PAST';
    return 'UPCOMING';
  };

  const eventsInCurrentMonth = useMemo(() => {
    return events
      .filter(event => {
        if (!event.startDate) return false;
        const eventDate = parseISO(event.startDate);
        return (
          eventDate.getMonth() === selectedDate.getMonth() &&
          eventDate.getFullYear() === selectedDate.getFullYear()
        );
      })
      .sort((a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime()); // Sort by date ascending
  }, [events, selectedDate, today]);

  const eventsToday = useMemo(() => {
    return events.filter(event => 
      event.startDate && isSameDay(parseISO(event.startDate), today)
    );
  }, [events, today]);


  // --- Calendar Tile Customization ---
  const getTileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
    
    // Check if the tile date has any event
    const eventsForDate = events.filter(event =>
      event.startDate && isSameDay(parseISO(event.startDate), date)
    );

    if (eventsForDate.length === 0) return null;

    const status = getEventStatus(eventsForDate[0].startDate); // Use status of the first event

    if (status === 'TODAY') return 'tile-today';
    if (status === 'PAST') return 'tile-past';
    if (status === 'UPCOMING') return 'tile-future';
    
    return null;
  };

  const handleActiveStartDateChange = ({ activeStartDate }) => {
    setSelectedDate(startOfMonth(activeStartDate));
  };
  
  // --- Rendering Helpers ---
  const renderEventCard = (event) => {
    const status = getEventStatus(event.startDate);
    const dateFormatted = format(parseISO(event.startDate), 'MMM dd, yyyy');

    return (
      <div key={event.id} className="event-card">
        <div className="event-info">
          <p className="event-title-type">
            {event.title} <span className={`status-tag tag-${status.toLowerCase()}`}>{status}</span>
          </p>
          <p className="event-description-text">{event.description}</p>
          <p className="event-date-text">
             <i className="far fa-calendar"></i> {dateFormatted}
          </p>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
        <section id="calendar" className="event-calendar-container loading-page">
            <h1 className="main-header-title">Event Calendar</h1>
            <p>Loading calendar events...</p>
        </section>
    );
  }

  return (
    <section id="calendar" className="event-calendar-container">
      <div className="main-header-content">
        <i className="fas fa-calendar-alt main-header-icon"></i>
        <h1 className="main-header-title">Event Calendar</h1>
        <p className="main-header-subtitle">DIET ACM Student Chapter</p>
      </div>

      <div className="calendar-content-wrapper">
        
        {/* 1. Left Panel: Calendar */}
        <div className="calendar-panel">
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            tileClassName={getTileClassName}
            showNeighboringMonth={false}
            next2Label={null}
            prev2Label={null}
            // Use custom nav content to remove default react-calendar look
            formatMonthYear={(locale, date) => format(date, 'MMMM yyyy')}
            onActiveStartDateChange={handleActiveStartDateChange}
            locale="en-US"
          />
          
          {/* Legend */}
          <div className="legend">
            <span className="legend-item"><span className="dot past"></span> Past Events</span>
            <span className="legend-item"><span className="dot today"></span> Today</span>
            <span className="legend-item"><span className="dot future"></span> Upcoming</span>
          </div>
        </div>

        {/* 2. Right Panel: Today & Monthly Events */}
        <div className="events-panel">
          
          {/* Events Today Block */}
          <div className="events-block today-block">
            <h3 className="block-title"><i className="far fa-clock"></i> Events Today</h3>
            <div className="event-list">
              {eventsToday.length > 0 ? (
                eventsToday.map(renderEventCard)
              ) : (
                <p className="no-events-msg">No events scheduled for today.</p>
              )}
            </div>
          </div>
          
          {/* Events in Month Block */}
          <div className="events-block monthly-block">
            <h3 className="block-title"><i className="fas fa-list-ul"></i> Events in {format(selectedDate, 'MMMM')}</h3>
            <div className="event-list">
              {eventsInCurrentMonth.length > 0 ? (
                eventsInCurrentMonth.map(renderEventCard)
              ) : (
                <p className="no-events-msg">No events scheduled for this month.</p>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default EventCalendar;